import React, { FC } from 'react';

import { useTranslation } from '@modernloop/i18n';
import type { DownloadReason } from '@modernloop/shared/components';
import { DownloadIcon, InfoIcon } from '@modernloop/shared/icons';
import { Box, Card, CardHeader, CircularProgress, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import type { Result } from 'neverthrow';

import { useDownloadReasonDialog } from 'src/components/Dialog/DownloadReasonDialog/useDownloadReasonDialog';
import type { DownloadFailure } from 'src/components/Dialog/DownloadReasonDialog/types';
import { useMetricsDownloadDescription } from './MetricsDownloadDateRangeContext';

/** Supplies section copy and the reason-aware action for one Metrics card. */
interface Props {
  children?: React.ReactNode;
  header: string;
  subHeader: string;
  onDownload?: (reason: DownloadReason) => Promise<Result<void, DownloadFailure>>;
  downloading?: boolean;
  infoTooltip?: string | JSX.Element;
}

/** Shows a Metrics card and opens the shared reason dialog before its CSV action. */
const SectionWrap: FC<Props> = ({ header, subHeader, onDownload, children, downloading, infoTooltip }) => {
  const { t } = useTranslation();
  const requestDownload = useDownloadReasonDialog();
  const dataDescription = useMetricsDownloadDescription(header);
  /** Starts the shared dialog for this section's current date range. */
  const handleDownload = () => {
    if (!onDownload) {
      return;
    }
    void requestDownload({ dataDescription, download: onDownload });
  };
  return (
    <Card style={{ boxShadow: 'none' }}>
      <CardHeader
        style={{
          padding: '8px',
        }}
        title={<Typography variant="subtitle1">{header}</Typography>}
        subheader={
          <Typography variant="body1" color="text.secondary" fontWeight={400}>
            {subHeader}
          </Typography>
        }
        action={
          <Stack direction="row" alignItems="center">
            {infoTooltip && (
              <Tooltip
                title={
                  <Typography variant="body2" color="text.primary">
                    {infoTooltip}
                  </Typography>
                }
              >
                <Box alignItems="center" display="flex">
                  <InfoIcon fontSize="medium" />
                </Box>
              </Tooltip>
            )}
            <IconButton
              aria-label={t('hiring-dashboard-metrics:metrics.download')}
              onClick={handleDownload}
              size="large"
            >
              {downloading ? <CircularProgress size="medium" /> : <DownloadIcon fontSize="medium" />}
            </IconButton>
          </Stack>
        }
      />
      <Box m={1} mt={0}>
        <Paper variant="outlined" sx={{ padding: 1.5 }}>
          {children}
        </Paper>
      </Box>
    </Card>
  );
};

export default SectionWrap;
